'use client';

import Link from '@eliadmin/core/src/components/link';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import type { SectionProps } from './custom-homepage';

export function FAQ({
  faqs,
  title,
  subtitle,
  description,
}: { faqs: TDynamicData[] } & Partial<SectionProps>) {
  const isDesktop = useBreakpoint('sm');

  const { t } = useTranslations();

  return (
    <section className={cn('custom-section', 'py-8 sm:py-10 lg:py-12')}>
      <div
        className={cn(
          'custom-container--xxl',
          'flex flex-col items-start justify-start gap-8 sm:gap-8 md:flex-row md:justify-between lg:gap-12',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-8 rounded-[20px] bg-primary-foreground p-4 sm:gap-10 sm:p-6 md:max-w-[518px] lg:gap-12 lg:p-8">
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <Heading title={title ?? ''} subtitle={subtitle} />
            <Paragraph innerHtml={description} />
          </div>
          <Button className="h-[50px] w-full sm:h-[52px] sm:w-fit" variant={'secondary'}>
            <Link href={ROUTES.CONTACT()}>{t('text186')}</Link>
          </Button>
        </div>
        <Accordion
          type="multiple"
          className="flex w-full flex-col items-start justify-start gap-0 md:max-w-[923px]"
        >
          {faqs.map(item => (
            <AccordionItem
              key={item.name}
              value={item.name}
              className={cn(
                'w-full border-b border-t-0 border-b-border py-4 sm:py-5 lg:py-6',
                'ea-faq__accordion-item',
              )}
            >
              <AccordionTrigger
                className={cn(
                  'flex w-full items-center justify-between gap-4 p-0',
                  'ea-faq__accordion-trigger',
                )}
              >
                <Heading
                  title={item.name}
                  tag={isDesktop ? 'h4' : 'h5'}
                  as="span"
                  className={{ title: 'text-left' }}
                />
              </AccordionTrigger>
              <AccordionContent className="w-full p-0 pt-4">
                <Paragraph
                  innerHtml={item.fields[0]?.value}
                  color="black"
                  size={isDesktop ? 'lg' : 'base'}
                  className="w-full"
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
